import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { films, navLinks, newsItems } from "@/mocks/home";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const filmHits = films
      .filter((f) => f.title.toLowerCase().includes(q))
      .map((f) => ({ id: f.id, title: f.title, kind: t("films.title"), href: "#films" }));
    const newsHits = newsItems
      .filter((n) => n.title.toLowerCase().includes(q))
      .map((n) => ({ id: n.id, title: n.title, kind: t("news.title"), href: "#news" }));
    return [...filmHits, ...newsHits].slice(0, 6);
  }, [query, t]);

  const solid = scrolled || menuOpen || searchOpen;

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (results.length > 0) {
      document.querySelector(results[0].href)?.scrollIntoView({ behavior: "smooth" });
      setSearchOpen(false);
      setQuery("");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-6">
        <a
          href="#home"
          className="flex items-center gap-3 cursor-pointer group"
        >
          <span
            className={`flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${
              solid
                ? "border-primary-300 bg-primary-100 text-primary-800"
                : "border-background-50/50 bg-background-50/10 text-background-50"
            }`}
          >
            <i className="ri-film-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg md:text-xl font-bold tracking-tight ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              {t("brand.name")}
            </span>
            <span
              className={`font-label text-[11px] md:text-xs uppercase tracking-[0.22em] ${
                solid ? "text-foreground-500" : "text-background-200"
              }`}
            >
              {t("brand.tagline")}
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              className={`font-label text-sm uppercase tracking-[0.14em] transition-colors cursor-pointer ${
                solid
                  ? "text-foreground-700 hover:text-primary-600"
                  : "text-background-100 hover:text-primary-200"
              }`}
            >
              {t(`nav.${link.id}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            aria-label={t("nav.search")}
            onClick={() => {
              setSearchOpen((v) => !v);
              setMenuOpen(false);
            }}
            className={`flex items-center justify-center w-10 h-10 rounded-md transition-colors cursor-pointer ${
              solid
                ? "text-foreground-700 hover:bg-background-100"
                : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className={searchOpen ? "ri-close-line text-xl" : "ri-search-line text-xl"}></i>
          </button>
          <a
            href="#support"
            className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-label text-sm uppercase tracking-[0.14em] px-4 md:px-5 py-2.5 rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-heart-3-line"></i>
            {t("nav.donate")}
          </a>
          <button
            type="button"
            aria-label={t("nav.menu")}
            onClick={() => {
              setMenuOpen((v) => !v);
              setSearchOpen(false);
            }}
            className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-md transition-colors cursor-pointer ${
              solid
                ? "text-foreground-800 hover:bg-background-100"
                : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="px-4 md:px-6 pb-4 bg-background-50 border-b border-background-200/70">
          <form onSubmit={handleSearchSubmit} className="relative max-w-xl mx-auto">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("nav.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            {query.trim() && (
              <div className="absolute left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-md overflow-hidden z-50 shadow-sm">
                {results.length > 0 ? (
                  results.map((r) => (
                    <a
                      key={`${r.kind}-${r.id}`}
                      href={r.href}
                      onClick={() => {
                        setSearchOpen(false);
                        setQuery("");
                      }}
                      className="flex items-center justify-between px-4 py-3 hover:bg-background-100 cursor-pointer"
                    >
                      <span className="text-sm text-foreground-900">{r.title}</span>
                      <span className="font-label text-xs uppercase tracking-wider text-foreground-400">
                        {r.kind}
                      </span>
                    </a>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-foreground-500">No matches found.</div>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-b border-background-200/70">
          <nav className="flex flex-col px-4 py-2">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-3.5 border-b border-background-200/60 last:border-0 font-label text-base uppercase tracking-[0.12em] text-foreground-800 hover:text-primary-600 cursor-pointer"
              >
                {t(`nav.${link.id}`)}
                <i className="ri-arrow-right-s-line text-lg text-foreground-300"></i>
              </a>
            ))}
            <a
              href="#support"
              onClick={() => setMenuOpen(false)}
              className="mt-3 mb-2 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-label text-sm uppercase tracking-[0.14em] px-5 py-3 rounded-md cursor-pointer"
            >
              <i className="ri-heart-3-line"></i>
              {t("nav.donate")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}